import {
  APP_BRANCHES,
  BILLING_CREDIT_OUTLET_STORAGE_KEY,
  canAccessAppBranch,
  getVisibleAppBranches,
  normalizeBranchValue,
} from "../config/appBranches";
import { collection, getDocs, limit, query, where } from "firebase/firestore";

/** Stable Firestore-safe doc id for customer_profiles from display name. */
export function customerProfileDocId(customerName) {
  const s = String(customerName || "").trim();
  if (!s) return "";
  try {
    return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return s.slice(0, 200).replace(/[/\\[\]#?]/g, "_");
  }
}

export const LOYALTY_TIERS = ["—", "Bronze", "Silver", "Gold", "Platinum"];

/** Same calendar day (local) for two dates */
export function sameLocalDay(a, b) {
  if (!(a instanceof Date) || !(b instanceof Date)) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * "Similar" amount: within ₹50 or 2% of the larger value.
 */
export function amountsSimilar(a, b) {
  const x = Number(a) || 0;
  const y = Number(b) || 0;
  if (x <= 0 || y <= 0) return false;
  const diff = Math.abs(x - y);
  const tol = Math.max(50, 0.02 * Math.max(x, y));
  return diff <= tol;
}

export function readStoredSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

/** Branch shop label for customer entry (e.g. Branch 3 → "Branch"), not login username. */
export function resolveCustomerEntryBranchLabel(storedUser = readStoredSessionUser()) {
  let branchValue = null;
  try {
    const fromStorage = localStorage.getItem(BILLING_CREDIT_OUTLET_STORAGE_KEY);
    if (fromStorage && canAccessAppBranch(storedUser, fromStorage)) {
      branchValue = normalizeBranchValue(fromStorage);
    }
  } catch {
    /* ignore */
  }
  if (!branchValue) {
    const visible = getVisibleAppBranches(storedUser);
    if (visible.length === 1) branchValue = visible[0].value;
  }
  if (!branchValue) return null;
  if (branchValue === "Branch 3") return "BRANCH";
  return APP_BRANCHES.find((b) => b.value === branchValue)?.label ?? null;
}

/** Legacy usernames saved before branch-label change (e.g. OFFER → BRANCH). */
export function formatCustomerEntryActorLabel(value) {
  const s = String(value ?? "").trim();
  if (!s) return s;
  if (s.toLowerCase() === "offer") return "BRANCH";
  return s;
}

export function customerEntryActorMatchesFilter(storedValue, filterValue) {
  if (!filterValue) return true;
  return formatCustomerEntryActorLabel(storedValue) === filterValue;
}

/** Saved on customer_entries.enteredBy — branch label when known, else Firestore username. */
export async function resolveCustomerEntryEnteredBy(auth, db) {
  const branchLabel = resolveCustomerEntryBranchLabel();
  if (branchLabel) return branchLabel;

  const user = auth?.currentUser;
  if (!user) return "Unknown";

  let entered = user.displayName || user.email || "Unknown";
  if (user.email && db) {
    try {
      const uq = query(collection(db, "users"), where("email", "==", user.email), limit(1));
      const us = await getDocs(uq);
      if (!us.empty) {
        const un = us.docs[0].data()?.username;
        if (un && String(un).trim()) entered = String(un).trim();
      }
    } catch {
      /* ignore */
    }
  }
  return entered;
}
